import { useState } from 'react';
import { COHORT_RULES, FORCE_RULES, QUARTER, RM_COHORTS, type RmCohort } from '../shared/q3';
import type { ForceStanding, ForceSummary, Standing, Summary, Team } from './standings';
import type { QuarterClock } from './useQuarterClock';
import Gauge from './Gauge';
import Countdown from './Countdown';
import { Link, to, toList } from './router';
import { COH, NearCard, nearEmptyNote, nearest } from './parts';
import { count, cr, pc, pc1 } from './format';

interface Props {
  rows: Standing[];
  summary: Summary[];
  teams: Team[];
  force: ForceStanding[];
  forceSummary: ForceSummary[];
  clock: QuarterClock;
  active: boolean;
}

const RACE_SEGMENTS: Array<[string, keyof Pick<Summary, 'qualified' | 'cat2Gap' | 'halfway' | 'underHalf'>]> = [
  ['q', 'qualified'],
  ['c', 'cat2Gap'],
  ['h', 'halfway'],
  ['r', 'underHalf'],
];

const TEAMS_PREVIEW = 8;

/** Which list each "Who's where" segment opens. */
const SEGMENT_LIST: Record<(typeof RACE_SEGMENTS)[number][1], string> = {
  qualified: 'qualified',
  cat2Gap: 'mandatory-short',
  halfway: 'past-halfway',
  underHalf: 'under-half',
};

const FORCE_SEGMENTS: Array<[string, keyof Pick<ForceSummary, 'qualified' | 'halfway' | 'underHalf'>]> = [
  ['q', 'qualified'],
  ['h', 'halfway'],
  ['r', 'underHalf'],
];

export function Hero({ clock }: { clock: QuarterClock }) {
  return (
    <header className="sk-hero" id="hero">
      <div className="sk-logo" role="img" aria-label="Shakti · Powering to 25K" />
      <div>
        <div className="sk-eyebrow">FundsIndia Private Wealth · Shakti contest series · {QUARTER.label}</div>
        <h1 className="sk-serif">
          <span className="sk-goldtext">Rise</span> to the
          <br />
          Next Level
        </h1>
        <p className="sk-sub">Two cohorts. Clear targets. One non-negotiable business mix.</p>
        <Countdown />
        <div className="sk-qprog">
          <span className="sk-live" />
          <span>Live</span>
          <span className="tr">
            <span className="fl" style={{ display: 'block', width: (clock.pace * 100).toFixed(1) + '%' }} />
          </span>
          <span>{pc(clock.pace)} of the quarter gone</span>
        </div>
      </div>
    </header>
  );
}

export default function HomeView({ rows, summary, teams, force, forceSummary, clock, active }: Props) {
  const of = (c: RmCohort | 'All') => summary.find((s) => s.cohort === c)!;
  const fAll = forceSummary.find((s) => s.group === 'All')!;
  const forceLeader = force.find((r) => r.rank === 1 && r.ranked);
  const all = of('All');
  // The team table starts at the top 8 so it sits level with the Top 5 beside it.
  const [allTeams, setAllTeams] = useState(false);
  const shownTeams = allTeams ? teams : teams.slice(0, TEAMS_PREVIEW);
  const top5 = rows.filter((r) => r.ranked).sort((a, b) => b.pct - a.pct).slice(0, 5);
  const near = nearest(rows);
  const onPace = rows.filter((r) => r.pct >= clock.pace).length;

  return (
    <div className="sk-view sk-home" id="home">
      <Hero clock={clock} />

      <div className="sk-overview" id="overview">
        <div className="sk-cohorts" id="cohorts" role="group" aria-label="Choose your cohort">
          {RM_COHORTS.map((c) => {
            const s = of(c);
            const leader = rows.find((r) => r.cohort === c && r.rank === 1 && r.ranked);
            return (
              <Link key={c} className={'sk-coh ' + COH[c].cls} to={to(c)} aria-label={`Open the ${COH[c].name} leaderboard`}>
                <div className={'img ' + COH[c].em} />
                <div className="body">
                  <div>
                    <div className="nm sk-goldtext">{COH[c].name}</div>
                    <div className="rule">
                      ₹{COHORT_RULES[c].target} Cr total · min ₹{COHORT_RULES[c].min} Cr Mandatory
                    </div>
                    <div className="lead">
                      Leading now: <b>{leader ? leader.rm : '—'}</b>
                    </div>
                    <span className="cta">View leaderboard →</span>
                  </div>
                  <div className="cnt">
                    <div className="big">{count(s.qualified)}</div>
                    <div className="lbl">qualified of {count(s.rms)}</div>
                  </div>
                </div>
              </Link>
            );
          })}
          <Link className="sk-coh force" to={to('Star Force')} aria-label="Open the Star Force leaderboard">
            <div className="img sk-em-force" />
            <div className="body">
              <div>
                <div className="nm sk-goldtext">Star Force</div>
                <div className="rule">
                  IC and Wealth Acquisition · qualify at {FORCE_RULES.qualifyAt * 100}% achieved
                </div>
                <div className="lead">
                  Leading now: <b>{forceLeader ? forceLeader.rm : '—'}</b>
                </div>
                <span className="cta">View leaderboard →</span>
              </div>
              <div className="cnt">
                <div className="big">{count(fAll.qualified)}</div>
                <div className="lbl">qualified of {count(fAll.members)}</div>
              </div>
            </div>
          </Link>
        </div>
        <p className="sk-tap">Tap your cohort to see your leaderboard</p>

        <section className="sk-sec sk-ovx" id="overall">
          <div className="sk-h2row">
            <div>
              <div className="sk-num">THE BIG PICTURE</div>
              <h2>Where Shakti stands today</h2>
            </div>
            <p className="sk-note">Both cohorts · business credited to date</p>
          </div>
          <div className="sk-big">
            <div className="sk-glass sk-gauge-card">
              <div className="sk-gauge">{active && <Gauge value={all.progress} pace={clock.pace} />}</div>
              <div className="sk-gline">
                <div>
                  <div className="l">Achieved</div>
                  <div className="v">{cr(all.totalCr)}</div>
                </div>
                <div>
                  <div className="l">Target</div>
                  <div className="v">{cr(all.targetCr)}</div>
                </div>
                <div>
                  <div className="l">Still to go</div>
                  <div className="v">{cr(all.toGoCr)}</div>
                </div>
              </div>
              <div className="sk-mix">
                <div className="top">
                  <span>Business mix</span>
                  <span>
                    Mandatory share <b>{pc1(all.cat2Share)}</b>
                  </span>
                </div>
                <div className="bar">
                  <div className="a" style={{ width: (all.totalCr ? all.coreCr / all.totalCr : 0) * 100 + '%' }} />
                  <div className="b" style={{ width: all.cat2Share * 100 + '%' }} />
                </div>
                <div className="key">
                  <span>
                    <i style={{ background: '#2fb88a' }} />
                    Core wealth <b>{cr(all.coreCr)}</b>
                  </span>
                  <span>
                    <i style={{ background: '#ef7a1a' }} />
                    Mandatory <b>{cr(all.cat2Cr)}</b>
                  </span>
                </div>
              </div>
            </div>
            <div className="sk-tiles">
              <Link className="sk-glass sk-tile" to={toList('qualified')} aria-label="See who is qualified">
                <svg className="ic" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M8 4h8v5a4 4 0 0 1-8 0z" />
                  <path d="M8 6H4a3 3 0 0 0 4 4M16 6h4a3 3 0 0 1-4 4" />
                  <path d="M12 13v4M9 17h6v4H9z" />
                </svg>
                <div className="l">Qualified</div>
                <div>
                  <div className="v sk-goldtext">{count(all.qualified)}</div>
                  <div className="s">
                    <b>{pc1(all.qualifiedShare)}</b> of {count(all.rms)} RMs
                  </div>
                </div>
              </Link>
              <Link className="sk-glass sk-tile" to={toList('on-pace')} aria-label="See who is on pace">
                <svg className="ic" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M13 2L4 14h7l-1 8 9-12h-7z" />
                </svg>
                <div className="l">On pace</div>
                <div>
                  <div className="v">{count(onPace)}</div>
                  <div className="s">RMs at or ahead of where today's pace says they should be</div>
                </div>
              </Link>
              <Link className="sk-glass sk-tile" to={toList('past-halfway')} aria-label="See who is past halfway">
                <svg className="ic" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 20V10M10 20V4M16 20v-8M22 20H2" />
                </svg>
                <div className="l">Past halfway</div>
                <div>
                  <div className="v">{count(all.halfway)}</div>
                  <div className="s">at 50%+ of target, not yet qualified</div>
                </div>
              </Link>
              <Link className="sk-glass sk-tile" to={toList('mandatory-short')} aria-label="See who is mandatory short">
                <svg className="ic" viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <circle cx="12" cy="12" r="5" />
                  <circle cx="12" cy="12" r="1.5" />
                </svg>
                <div className="l">Mandatory short</div>
                <div>
                  <div className="v">{count(all.cat2Gap)}</div>
                  <div className="s">total target met, mandatory mix still pending</div>
                </div>
              </Link>
            </div>
          </div>
        </section>

        <section className="sk-sec sk-ovx" id="race-status">
          <div className="sk-h2row">
            <div>
              <div className="sk-num">THE RACE</div>
              <h2>Who's where</h2>
            </div>
            <div className="sk-key">
              <span>
                <i style={{ background: '#e7c66d' }} />
                Qualified
              </span>
              <span>
                <i style={{ background: '#ef7a1a' }} />
                Target met, Mandatory short
              </span>
              <span>
                <i style={{ background: 'rgba(255,255,255,.35)' }} />
                50%+ of target
              </span>
              <span>
                <i style={{ background: 'rgba(255,255,255,.12)' }} />
                Under 50%
              </span>
            </div>
          </div>
          <div className="sk-glass sk-race">
            {RM_COHORTS.map((c) => {
              const s = of(c);
              return (
                <div className="sk-rc" key={c}>
                  <div className="h">
                    {COH[c].name} <span>{s.rms + ' RMs · ' + pc(s.progress) + ' of cohort target'}</span>
                  </div>
                  <div className="bar">
                    {RACE_SEGMENTS.filter(([, k]) => s[k] > 0).map(([cls, k]) => (
                      <Link
                        key={k}
                        className={'seg ' + cls}
                        to={toList(SEGMENT_LIST[k], c)}
                        style={{ flex: `${s[k]} 0 0` }}
                        title={`See these ${s[k]} ${COH[c].name}`}
                      >
                        {s[k]}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
            <div className="sk-rc sk-rcf">
              <div className="h">
                Star Force <span>{fAll.members + ' members · ' + fAll.qualified + ' qualified'}</span>
              </div>
              <div className="bar">
                {FORCE_SEGMENTS.filter(([, k]) => fAll[k] > 0).map(([cls, k]) => (
                  <div key={k} className={'seg ' + cls} style={{ flex: `${fAll[k]} 0 0` }}>
                    {fAll[k]}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="sk-sec sk-ovx" id="hall-of-fame">
          <div className="sk-duo">
            <div>
              <div className="sk-h2row">
                <div>
                  <div className="sk-num">HALL OF FAME</div>
                  <h2>Shakti Top 5</h2>
                </div>
                <p className="sk-note">Highest % of target, both cohorts</p>
              </div>
              <div className="sk-glass sk-stars">
                {top5.map((r, i) => (
                  <div className="sk-star" key={r.empId}>
                    <div className="n">{i + 1}</div>
                    <div>
                      <div className="nm">{r.rm}</div>
                      <div className="sub">
                        <span className={'ch ' + COH[r.cohort].cls}>{COH[r.cohort].name}</span>
                        <span>{r.loc + ' · ' + cr(r.totalCr)}</span>
                      </div>
                    </div>
                    <div className="p">
                      {pc(r.pct)}
                      <small>of target</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="sk-h2row" id="team-league">
                <div>
                  <div className="sk-num">TEAM LEAGUE</div>
                  <h2>P&amp;L head standings</h2>
                </div>
                <p className="sk-note">Team achieved vs team target</p>
              </div>
              <div className="sk-glass sk-teams">
                <table>
                  <thead>
                    <tr>
                      <th>P&amp;L head</th>
                      <th className="num hide-s">RMs</th>
                      <th className="num">Qualified</th>
                      <th className="num hide-s">Achieved</th>
                      <th>Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownTeams.map((t) => (
                      <tr key={t.head}>
                        <td>{t.head}</td>
                        <td className="num hide-s">{t.rms}</td>
                        <td className="num">{t.qualified}</td>
                        <td className="num hide-s">{cr(t.totalCr)}</td>
                        <td>
                          <div className="tb">
                            <div className="tr">
                              <div className="fl" style={{ width: Math.min(t.progress, 1) * 100 + '%' }} />
                            </div>
                            <span>{pc(t.progress)}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {teams.length > TEAMS_PREVIEW && (
                  <button className="sk-more" onClick={() => setAllTeams((v) => !v)} aria-expanded={allTeams}>
                    {allTeams ? 'Show top ' + TEAMS_PREVIEW : 'Show all ' + teams.length + ' teams'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="sk-sec sk-ovx" id="almost-there">
          <div className="sk-h2row">
            <div>
              <div className="sk-num">ONE PUSH AWAY</div>
              <h2>Closest to qualifying</h2>
            </div>
            <p className="sk-note">Not yet qualified, both cohorts</p>
          </div>
          <div className="sk-strike">
            {near.map((r) => (
              <NearCard key={r.empId} r={r} showCohort />
            ))}
            {!near.length && <p className="sk-note">{nearEmptyNote(rows, 'Everyone has qualified!')}</p>}
          </div>
        </section>
      </div>
    </div>
  );
}
